import { DefaultError, QueryClient, QueryKey, useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { magpCMSApi } from "~/Services";

interface LoginPage {
  id: number;
  title: string;
  field_welcome_page_image: string;
  field_welcome_text: string;
  field_welcome_ms: string;
  field_welcome_zh: string;
  field_welcome_procurement_text: string;
  field_welcome_procurement_ms: string;
  field_welcome_procurement_zh: string;
  field_welcome_guidelines_text: string;
  field_welcome_guidelines_ms: string;
  field_welcome_guidelines_zh: string;
  field_guest_login_text: string;
  field_guest_login_text_malay: string;
  field_guest_login_text_zh: string;
  field_vendor_login_text: string;
  field_vendor_login_text_ms: string;
  field_vendor_login_text_zh: string;
  field_staff_login_text: string;
  field_staff_login_text_ms: string;
  field_staff_login_text_zh: string;
}

interface MobileLoginPage {
  id: number;
  title: string;
  field_malay_calendar_header_text: string;
  field_mandarin_calendar_header_t: string;
  field_welcome_calendar_text: string;
  field_malay_welcome_calendar_tex: string;
  field_mandarin_welcome_calendar: string;
  field_welcome_calendar_descripti: string;
  field_malay_welcome_calendar_des: string;
  field_mandari_welco_calen_descri: string;
  field_calendar_welcom_continue: string;
  field_malay_continue_text: string;
  field_mandarin_continue_text: string;
  field_magp_image: string;
  published: boolean;
  createdAt: boolean | string;
  updatedAt: string;
}

interface WebLoginPage {
  id: number;
  field_web_login_page_link: string;
  field_web_login_page_title_en: string;
  field_web_login_page_title_ms: string;
  field_web_login_page_title_zh: string;
  field_web_login_page_subtitle_en: string;
  field_web_login_page_subtitle_ms: string;
  field_web_login_page_subtitle_zh: string;
  image: string;
  field_created_at: string;
  field_published: boolean;
  field_updated_at: string;
  field_web_login_page_image: string;
  field_sub_title_colour: string;
  field_title_colour: string;
}

const useMobileLoginPage = (
  options?: UseQueryOptions<LoginPage[], DefaultError, LoginPage[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<LoginPage[], DefaultError> => {
  return useQuery<LoginPage[], DefaultError, LoginPage[], QueryKey>(
    {
      queryKey: ["mobileLoginPage"],
      enabled: false,
      queryFn: async (): Promise<LoginPage[]> => {
        const response = await magpCMSApi.get<LoginPage[]>(`/api/welcome-login-page/all-fields`);
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useMobileCalendarPage = (
  options?: UseQueryOptions<MobileLoginPage[], DefaultError, MobileLoginPage[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<MobileLoginPage[], DefaultError> => {
  return useQuery<MobileLoginPage[], DefaultError, MobileLoginPage[], QueryKey>(
    {
      queryKey: ["mobileCalendarPage"],
      enabled: false,
      queryFn: async (): Promise<MobileLoginPage[]> => {
        const response = await magpCMSApi.get<MobileLoginPage[]>(`/api/calendar-welcome/all-fields`);
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useWebLoginPage = (
  id?: any,
  options?: UseQueryOptions<WebLoginPage[], DefaultError, WebLoginPage[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<WebLoginPage[], DefaultError> => {
  return useQuery<WebLoginPage[], DefaultError, WebLoginPage[], QueryKey>(
    {
      queryKey: ["webLoginPage", id],
      enabled: false,
      queryFn: async (): Promise<WebLoginPage[]> => {
        const response = await magpCMSApi.get<WebLoginPage[]>(`/api/web-auth/all-fields?id=${id}`);
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useMainLoginPage = (
  options?: UseQueryOptions<WebLoginPage[], DefaultError, WebLoginPage[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<WebLoginPage[], DefaultError> => {
  return useQuery<WebLoginPage[], DefaultError, WebLoginPage[], QueryKey>(
    {
      queryKey: ["mainLoginPage"],
      enabled: false,
      queryFn: async (): Promise<WebLoginPage[]> => {
        const response = await magpCMSApi.get<WebLoginPage[]>(`/api/web-auth/all-fields?id=387`);
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useStaffLoginPage = (
  options?: UseQueryOptions<WebLoginPage[], DefaultError, WebLoginPage[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<WebLoginPage[], DefaultError> => {
  return useQuery<WebLoginPage[], DefaultError, WebLoginPage[], QueryKey>(
    {
      queryKey: ["vendorLoginPage"],
      enabled: false,
      queryFn: async (): Promise<WebLoginPage[]> => {
        const response = await magpCMSApi.get<WebLoginPage[]>(`/api/web-auth/all-fields?id=388`);
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useForgotPassword = (
  options?: UseQueryOptions<WebLoginPage[], DefaultError, WebLoginPage[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<WebLoginPage[], DefaultError> => {
  return useQuery<WebLoginPage[], DefaultError, WebLoginPage[], QueryKey>(
    {
      queryKey: ["forgotPassword"],
      enabled: false,
      queryFn: async (): Promise<WebLoginPage[]> => {
        const response = await magpCMSApi.get<WebLoginPage[]>(`/api/web-auth/all-fields?id=389`);
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useSignUp = (
  options?: UseQueryOptions<WebLoginPage[], DefaultError, WebLoginPage[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<WebLoginPage[], DefaultError> => {
  return useQuery<WebLoginPage[], DefaultError, WebLoginPage[], QueryKey>(
    {
      queryKey: ["signUp"],
      enabled: false,
      queryFn: async (): Promise<WebLoginPage[]> => {
        const response = await magpCMSApi.get<WebLoginPage[]>(`/api/web-auth/all-fields?id=390`);
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

export {
  useForgotPassword,
  useMainLoginPage,
  useMobileCalendarPage,
  useMobileLoginPage,
  useSignUp,
  useStaffLoginPage,
  useWebLoginPage,
};
